/**
 * ExhibitorsManageTab
 * ────────────────────
 * Lists all exhibitors for this event. Supports Add, Edit, Delete.
 * API:  GET/POST/PUT/DELETE  /exhibitors
 *
 * Fields shown: logo, name, slug, tagline, category, order, isActive.
 * Editing opens a right-side drawer with the main identity fields.
 */
import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  Box, Typography, Button, Card, IconButton, TextField,
  Alert, Drawer, Avatar, Chip, Tooltip, Table, TableHead,
  TableRow, TableCell, TableBody, TableContainer, Switch,
  FormControlLabel, Select, MenuItem, FormControl, Skeleton,
} from '@mui/material';
import {
  AddOutlined, EditOutlined, DeleteOutlined, CloseOutlined,
  SaveOutlined, StoreOutlined, SearchOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../../../hooks/useApi';
import { PRIMARY, INPUT_SX, FormField, SectionCard, ImageUploader } from '../shared';

// ── Status chip ───────────────────────────────────────────────────────────────
const StatusChip = ({ active }) => (
  <Chip label={active ? 'Active' : 'Inactive'} size="small" sx={{
    height: 20, fontSize: '0.7rem',
    background: active ? alpha('#00a882', 0.08) : alpha('#888', 0.08),
    color: active ? '#00a882' : '#888',
  }} />
);

// ── Exhibitor drawer form ─────────────────────────────────────────────────────
const BLANK_EX = {
  name: '', slug: '', tagline: '', description: '',
  category: '', order: 0, logoUrl: '', boothThumbnail: '',
  website: '', email: '', phone: '', isActive: true,
  boothBgColor: '#0D1B2A', accentColor: '',
};

function ExhibitorDrawer({ open, exhibitor, eventId, onClose, onSaved }) {
  const [form, setForm] = useState({ ...BLANK_EX });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (exhibitor) setForm({ ...BLANK_EX, ...exhibitor });
    else           setForm({ ...BLANK_EX });
    setError('');
  }, [exhibitor, open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, event: eventId };
      if (exhibitor?._id) {
        const r = await api.put(`/exhibitors/${exhibitor._id}`, payload);
        onSaved(r.data.data);
      } else {
        const r = await api.post('/exhibitors', payload);
        onSaved(r.data.data);
      }
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save exhibitor');
    } finally { setSaving(false); }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 520 }, display: 'flex', flexDirection: 'column' } }}>

      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {exhibitor ? 'Edit Exhibitor' : 'Add Exhibitor'}
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseOutlined sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <SectionCard title="Identity">
          <FormField label="Company Name" required>
            <TextField value={form.name} onChange={e => set('name', e.target.value)}
              fullWidth size="small" placeholder="Acme Corp" sx={INPUT_SX} />
          </FormField>
          <FormField label="Slug" hint="URL-safe identifier">
            <TextField
              value={form.slug}
              onChange={e => set('slug', e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
              fullWidth size="small" placeholder="acme-corp" sx={INPUT_SX}
            />
          </FormField>
          <FormField label="Tagline">
            <TextField value={form.tagline || ''} onChange={e => set('tagline', e.target.value)}
              fullWidth size="small" sx={INPUT_SX} />
          </FormField>
          <FormField label="Description">
            <TextField value={form.description || ''} onChange={e => set('description', e.target.value)}
              fullWidth size="small" multiline rows={3} sx={INPUT_SX} />
          </FormField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormField label="Category">
                <TextField value={form.category || ''} onChange={e => set('category', e.target.value)}
                  fullWidth size="small" placeholder="Technology" sx={INPUT_SX} />
              </FormField>
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormField label="Display Order">
                <TextField type="number" value={form.order ?? 0} onChange={e => set('order', Number(e.target.value))}
                  fullWidth size="small" sx={INPUT_SX} />
              </FormField>
            </Box>
          </Box>
        </SectionCard>

        <SectionCard title="Contact">
          <FormField label="Website">
            <TextField value={form.website || ''} onChange={e => set('website', e.target.value)}
              fullWidth size="small" placeholder="https://acme.com" sx={INPUT_SX} />
          </FormField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormField label="Email">
                <TextField value={form.email || ''} onChange={e => set('email', e.target.value)}
                  fullWidth size="small" sx={INPUT_SX} />
              </FormField>
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormField label="Phone">
                <TextField value={form.phone || ''} onChange={e => set('phone', e.target.value)}
                  fullWidth size="small" sx={INPUT_SX} />
              </FormField>
            </Box>
          </Box>
        </SectionCard>

        <SectionCard title="Booth Visuals">
          <FormField label="Logo">
            <Box sx={{ maxWidth: 140 }}>
              <ImageUploader value={form.logoUrl || ''} onChange={v => set('logoUrl', v)}
                aspectRatio="1/1" label="Upload Logo" hint="PNG, JPG, SVG" />
            </Box>
          </FormField>
          <FormField label="Booth Thumbnail" hint="Card preview image in the exhibitor grid">
            <ImageUploader value={form.boothThumbnail || ''} onChange={v => set('boothThumbnail', v)}
              aspectRatio="16/9" label="Upload Thumbnail" />
          </FormField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormField label="Booth BG Color">
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box component="input" type="color" value={form.boothBgColor || '#0D1B2A'}
                    onChange={e => set('boothBgColor', e.target.value)}
                    style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: 8, padding: 2 }} />
                  <TextField value={form.boothBgColor || ''} onChange={e => set('boothBgColor', e.target.value)}
                    size="small" sx={{ ...INPUT_SX, flex: 1 }} />
                </Box>
              </FormField>
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormField label="Accent Color">
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box component="input" type="color" value={form.accentColor || '#ffffff'}
                    onChange={e => set('accentColor', e.target.value)}
                    style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: 8, padding: 2 }} />
                  <TextField value={form.accentColor || ''} onChange={e => set('accentColor', e.target.value)}
                    size="small" sx={{ ...INPUT_SX, flex: 1 }} />
                </Box>
              </FormField>
            </Box>
          </Box>
        </SectionCard>

        <SectionCard title="Status">
          <FormControlLabel
            control={
              <Switch checked={!!form.isActive} onChange={e => set('isActive', e.target.checked)} size="small"
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#00a882' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { background: '#00a882' } }} />
            }
            label={
              <Box>
                <Box component="span" sx={{ fontWeight: 600, fontSize: '0.875rem', display: 'block' }}>Active</Box>
                <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Visible to attendees</Box>
              </Box>
            }
          />
        </SectionCard>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e8edf2', flexShrink: 0, background: '#fafbfc' }}>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveOutlined />} disabled={saving}
            sx={{ borderRadius: 2, background: PRIMARY, '&:hover': { background: '#0d5fa8' } }}>
            {saving ? 'Saving…' : 'Save Exhibitor'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const ExhibitorsManageTab = forwardRef(function ExhibitorsManageTab({ event, isActive = false }, ref) {
  useImperativeHandle(ref, () => ({ save: null, saving: false, saved: false }));

  const [exhibitors, setExhibitors] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing]       = useState(null);
  const [deleteId, setDeleteId]     = useState(null);

  const load = useCallback(async () => {
    if (!event?._id) return;
    setLoading(true); setError('');
    try {
      const r = await api.get(`/exhibitors?eventId=${event._id}`);
      setExhibitors(r.data.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load exhibitors');
    } finally { setLoading(false); }
  }, [event?._id]);

  useEffect(() => { load(); }, [load]);

  // Refetch when the user navigates back to this tab (skip initial mount)
  const mountedRef = React.useRef(false);
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    if (isActive) load();
  }, [isActive]); // eslint-disable-line

  const openAdd  = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (ex) => { setEditing(ex); setDrawerOpen(true); };

  const handleSaved = (ex) => {
    setExhibitors(prev => {
      const idx = prev.findIndex(e => e._id === ex._id);
      return idx >= 0 ? prev.map((e, i) => i === idx ? ex : e) : [...prev, ex];
    });
  };

  const confirmDelete = async (id) => {
    try {
      await api.delete(`/exhibitors/${id}`);
      setExhibitors(prev => prev.filter(e => e._id !== id));
    } catch (e) {
      setError(e?.response?.data?.message || 'Delete failed');
    } finally { setDeleteId(null); }
  };

  const filtered = exhibitors
    .filter(ex => !search || ex.name.toLowerCase().includes(search.toLowerCase()) || (ex.category || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name));

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1a1a2e' }}>
          Exhibitors
          {!loading && <Chip label={exhibitors.length} size="small" sx={{ ml: 1, height: 20, fontSize: '0.72rem', background: alpha(PRIMARY, 0.08), color: PRIMARY }} />}
        </Typography>
        <Button size="small" variant="contained" startIcon={<AddOutlined />} onClick={openAdd}
          sx={{ borderRadius: 2, background: PRIMARY, '&:hover': { background: '#0d5fa8' }, textTransform: 'none' }}>
          Add Exhibitor
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2.5 }}>
        <TextField
          value={search} onChange={e => setSearch(e.target.value)}
          fullWidth size="small" placeholder="Search exhibitors…"
          sx={INPUT_SX}
          InputProps={{ startAdornment: <SearchOutlined sx={{ fontSize: 18, color: 'text.secondary', mr: 0.75 }} /> }}
        />
      </Box>

      {/* Table */}
      <Card sx={{ borderRadius: 3, border: '1px solid #e8edf2', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            {[...Array(4)].map((_, i) => <Skeleton key={i} height={52} sx={{ mb: 1, borderRadius: 2 }} />)}
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 9 }}>
            <StoreOutlined sx={{ fontSize: 48, color: alpha(PRIMARY, 0.15), mb: 2 }} />
            <Typography sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              {search ? 'No exhibitors match your search' : 'No exhibitors yet'}
            </Typography>
            {!search && (
              <Button variant="contained" startIcon={<AddOutlined />} onClick={openAdd} sx={{ mt: 2, borderRadius: 2, background: PRIMARY }}>
                Add First Exhibitor
              </Button>
            )}
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f8f9fb' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Exhibitor</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Order</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(ex => (
                  <TableRow key={ex._id} hover
                    sx={{ '&:last-child td': { borderBottom: 0 }, cursor: 'pointer' }}
                    onClick={() => openEdit(ex)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {ex.logoUrl
                          ? <Box component="img" src={ex.logoUrl} alt={ex.name}
                              sx={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 1.5, border: `1px solid ${alpha(PRIMARY, 0.1)}` }} />
                          : <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY, fontSize: '0.85rem', fontWeight: 700 }}>
                              {ex.name?.[0]}
                            </Avatar>
                        }
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{ex.name}</Typography>
                          <Typography variant="caption" color="text.secondary">/{ex.slug}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{ex.category || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{ex.order ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip active={ex.isActive} />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); openEdit(ex); }}
                            sx={{ color: '#c0c8d4', '&:hover': { color: PRIMARY } }}>
                            <EditOutlined sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); confirmDelete(ex._id); }}
                            sx={{ color: '#c0c8d4', '&:hover': { color: '#d32f2f' } }}>
                            <DeleteOutlined sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <ExhibitorDrawer
        open={drawerOpen}
        exhibitor={editing}
        eventId={event?._id}
        onClose={() => setDrawerOpen(false)}
        onSaved={handleSaved}
      />
    </Box>
  );
});

export default ExhibitorsManageTab;